import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

// Drop the exported hero film at frontend/public/hero-loop.mp4 and it
// automatically replaces the slideshow background (see /app/HERO_VIDEO_BRIEF.md).
// WebM/VP9 is the lightweight fallback for browsers without H.264.

const MaskedHeadline = ({ text, slideKey }) => (
  <h1
    data-testid="hero-headline"
    className="font-serif text-5xl font-semibold leading-[1.04] tracking-tight text-ivory sm:text-6xl lg:text-7xl"
  >
    {text.split(" ").map((word, i) => (
      <span key={`${slideKey}-${i}`} className="inline-block overflow-hidden pb-1 align-bottom">
        <motion.span
          className="inline-block"
          initial={{ y: "115%" }}
          animate={{ y: 0 }}
          transition={{ duration: 0.9, delay: 0.25 + i * 0.07, ease: EASE }}
        >
          {word}
          {i < text.split(" ").length - 1 ? " " : ""}
        </motion.span>
      </span>
    ))}
  </h1>
);

export const Hero = ({ slides }) => {
  const [index, setIndex] = useState(0);
  // Video-first: the hero film is a permanent fixture, so paint the video layout
  // immediately (poster shows at once) — never flash the static slideshow first.
  const [hasVideo, setHasVideo] = useState(true);
  const sectionRef = useRef(null);
  const count = slides.length;
  const slide = slides[index] || slides[0];

  useEffect(() => {
    if (count < 2 || hasVideo) return undefined;
    const t = setInterval(() => setIndex((i) => (i + 1) % count), 7000);
    return () => clearInterval(t);
  }, [count, hasVideo]);

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [4, -4]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-5, 5]), { stiffness: 120, damping: 18 });

  const onMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <section
      ref={sectionRef}
      data-testid="hero-section"
      className="relative overflow-hidden bg-concordia-ink"
      onMouseMove={onMouseMove}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
    >
      {/* Optional generated hero film — activates automatically when the file exists */}
      <motion.video
        data-testid="hero-video"
        className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${hasVideo ? "opacity-50" : "opacity-0"}`}
        style={{ y: videoY }}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        poster="/hero-poster.jpg"
        onCanPlay={(e) => {
          if (e.currentTarget.videoWidth > 0) setHasVideo(true);
        }}
        onError={() => setHasVideo(false)}
        aria-hidden="true"
      >
        <source src="/hero-loop.webm" type="video/webm" />
        <source src="/hero-loop.mp4" type="video/mp4" onError={() => setHasVideo(false)} />
      </motion.video>
      {hasVideo && (
        <div
          className="absolute inset-0 bg-gradient-to-r from-concordia-ink via-concordia-ink/70 to-concordia-ink/25"
          aria-hidden="true"
        />
      )}
      <div className="noise-overlay" aria-hidden="true" />
      <div
        className="pointer-events-none absolute -right-40 -top-40 h-[36rem] w-[36rem] rounded-full bg-concordia-mist/30 blur-3xl"
        aria-hidden="true"
      />
      <div className="relative mx-auto grid min-h-[100svh] max-w-7xl items-center gap-14 px-4 pb-20 pt-32 sm:px-6 lg:grid-cols-12 lg:gap-8 lg:px-8">
        <div className={hasVideo ? "lg:col-span-9" : "lg:col-span-7"}>
          <AnimatePresence mode="wait">
            <motion.div
              key={slide.id ?? index}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, transition: { duration: 0.35 } }}
            >
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
                className="mb-7 flex items-center gap-4"
              >
                <span className="h-px w-10 bg-brass" aria-hidden="true" />
                <span className="font-mono text-[0.65rem] uppercase tracking-[0.32em] text-brass">
                  {slide.eyebrow}
                </span>
              </motion.div>

              <MaskedHeadline text={slide.title} slideKey={slide.id ?? index} />

              <motion.p
                data-testid="hero-subtitle"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
                className="mt-7 max-w-xl text-base leading-relaxed text-ivory/70 sm:text-lg"
              >
                {slide.subtitle}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
                className="mt-10 flex flex-wrap items-center gap-4"
              >
                <Link
                  to="/apply"
                  data-testid="hero-apply-button"
                  className="group inline-flex items-center gap-2 rounded-full bg-ivory px-8 py-4 text-sm font-semibold text-concordia transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_rgba(0,0,0,0.35)]"
                >
                  Apply Now
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link
                  to={slide.cta_href || "/emi-calculator"}
                  data-testid="hero-secondary-cta"
                  className="inline-flex items-center gap-2 rounded-full border border-ivory/25 px-8 py-4 text-sm font-semibold text-ivory transition-colors duration-300 hover:border-brass hover:text-brass"
                >
                  {slide.cta_label || "Calculate your EMI"}
                </Link>
              </motion.div>
            </motion.div>
          </AnimatePresence>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-14 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[0.68rem] uppercase tracking-[0.22em] text-ivory/45"
          >
            <span data-testid="hero-stat-products">Loans & credit cards</span>
            <span className="h-1 w-1 rotate-45 bg-brass/70" aria-hidden="true" />
            <span data-testid="hero-stat-partners">Trusted lending partners</span>
            <span className="h-1 w-1 rotate-45 bg-brass/70" aria-hidden="true" />
            <span data-testid="hero-stat-rates">Indicative rates from 7.40% p.a.*</span>
          </motion.div>

          {count > 1 && !hasVideo && (
            <div className="mt-10 flex items-center gap-2" role="tablist" aria-label="Hero slides">
              {slides.map((s, i) => (
                <button
                  key={s.id ?? i}
                  data-testid={`hero-slide-indicator-${i}`}
                  onClick={() => setIndex(i)}
                  aria-label={`Slide ${i + 1}`}
                  className={`h-[3px] rounded-full transition-all duration-500 ${
                    i === index ? "w-12 bg-brass" : "w-6 bg-ivory/25 hover:bg-ivory/50"
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {!hasVideo && (
          <div className="relative lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
              style={{ rotateX, rotateY, transformPerspective: 1000 }}
              className="relative"
            >
              <div className="absolute -left-4 -top-4 h-full w-full rounded-b-[2rem] rounded-t-[10rem] border border-brass/50" aria-hidden="true" />
              <div className="relative aspect-[4/5] overflow-hidden rounded-b-[2rem] rounded-t-[10rem]">
                <AnimatePresence mode="sync">
                  <motion.img
                    key={slide.image_url}
                    src={slide.image_url}
                    alt="An Indian family in their modern home, representing aspirations financed by Loan Salaah"
                    data-testid="hero-image"
                    initial={{ opacity: 0, scale: 1.06 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1.2, ease: EASE }}
                    style={{ y: imageY }}
                    className="h-[112%] w-full object-cover"
                  />
                </AnimatePresence>
                <div className="absolute inset-0 bg-gradient-to-t from-concordia-ink/50 via-transparent to-transparent" aria-hidden="true" />
              </div>
              <div className="absolute -bottom-6 -right-3 rounded-2xl border border-brass/30 bg-ivory px-6 py-4 shadow-[0_20px_50px_rgba(0,0,0,0.35)] sm:-right-6">
                <p className="font-mono text-[0.6rem] uppercase tracking-[0.25em] text-slate">Home loans from</p>
                <p className="mt-1 font-serif text-3xl font-semibold text-concordia" data-testid="hero-rate-badge">7.40%<span className="text-base text-slate"> p.a.*</span></p>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </section>
  );
};
